import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  styled,
  CircularProgress,
  Slide,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Stack,
  Card,
  Button,
  Divider,
  TextField,
  Tooltip,
  Fade,
  useTheme,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import StripeIcon from "../../images/stripe-icon.svg";
import BinanceIcon from "../../images/binance-icon.svg";
import useApp from "../../store/contexts/AppContext";
import axios from "axios";

// Animation for dialog entrance
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Styled Dialog component
const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: 16,
    boxShadow: theme.palette.mode === "dark"
      ? "0 8px 32px rgba(0, 0, 0, 0.6)"
      : "0 8px 32px rgba(0, 0, 0, 0.15)",
    maxWidth: 540,
    width: "100%",
    backgroundColor: theme.palette.background.paper,
  },
  "& .MuiDialogTitle-root": {
    borderBottom: "1px solid",
    borderColor: theme.palette.divider,
    padding: theme.spacing(3),
    background: theme.palette.mode === "dark"
      ? "linear-gradient(135deg, rgba(212, 68, 2, 0.1) 0%, rgba(212, 68, 2, 0.05) 100%)"
      : "linear-gradient(135deg, rgba(212, 68, 2, 0.05) 0%, rgba(212, 68, 2, 0.02) 100%)",
  },
  "& .MuiDialogContent-root": {
    padding: theme.spacing(3),
  },
}));

// Styled package card - compact and cool
const PackageCard = styled(Card)(({ theme, isselected }) => ({
  minWidth: 110,
  maxWidth: 120,
  cursor: "pointer",
  padding: theme.spacing(1.5),
  textAlign: "center",
  position: "relative",
  borderRadius: 10,
  border: `2px solid ${
    isselected === "true"
      ? theme.palette.primary.main
      : theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.12)"
        : "rgba(0, 0, 0, 0.12)"
  }`,
  backgroundColor: isselected === "true"
    ? theme.palette.mode === "dark"
      ? "rgba(212, 68, 2, 0.1)"
      : "rgba(212, 68, 2, 0.05)"
    : theme.palette.mode === "dark"
    ? "rgba(255, 255, 255, 0.04)"
    : "rgba(255, 255, 255, 0.8)",
  backdropFilter: "blur(10px)",
  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
  "&:hover": {
    borderColor: theme.palette.primary.main,
    transform: "translateY(-2px)",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
  },
}));

// Styled package scroll container
const PackageScrollContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1.5),
  overflowX: "auto",
  padding: theme.spacing(1.5, 0.5, 2, 0.5),
  scrollBehavior: "smooth",
  "&::-webkit-scrollbar": {
    height: 6,
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.05)"
      : "rgba(0, 0, 0, 0.05)",
    borderRadius: 3,
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: theme.palette.primary.main,
    borderRadius: 3,
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
}));

// Styled payment method button
const PaymentMethodButton = styled(Button)(({ theme, isselected }) => ({
  borderRadius: 10,
  padding: theme.spacing(1.5, 2),
  justifyContent: "flex-start",
  backgroundColor:
    isselected === "true"
      ? theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.1)"
        : "rgba(0, 0, 0, 0.04)"
      : "transparent",
  border: `1px solid ${
    isselected === "true" ? theme.palette.primary.main : theme.palette.divider
  }`,
  "&:hover": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.08)"
        : "rgba(0, 0, 0, 0.03)",
    border: `1px solid ${
      isselected === "true"
        ? theme.palette.primary.main
        : theme.palette.action.hover
    }`,
  },
  "& .MuiButton-startIcon": {
    marginRight: theme.spacing(1.5),
  },
}));

// Format numbers with commas
const formatNumber = (num) => {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

export default function PaymentCheckoutModal({
  openPaymentCheckout,
  handlePaymentCheckoutClose,
}) {
  const theme = useTheme();
  const { showSnackbar, handleStripePaymentCheckout } = useApp();

  // State
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("stripe");
  const [customAmount, setCustomAmount] = useState(2.00); // Changed to number for dollar amount
  const [displayAmount, setDisplayAmount] = useState("2.00"); // Formatted display value
  const [calculatedCoins, setCalculatedCoins] = useState(200); // Coins calculated from amount
  const [processing, setProcessing] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(0);
  const [coinPackages, setCoinPackages] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(true);
  const [packagesError, setPackagesError] = useState(null);

  // Fetch coin packages from backend
  useEffect(() => {
    const fetchCoinPackages = async () => {
      try {
        setLoadingPackages(true);
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/economy/coin-packages`
        );

        if (response.data && response.data.data && response.data.data.coin_packages) {
          const packages = response.data.data.coin_packages;
          setCoinPackages(packages);

          // Initialize with first package if available
          if (packages.length > 0) {
            setCustomAmount(packages[0].price);
            setDisplayAmount(packages[0].price.toFixed(2));
            setCalculatedCoins(packages[0].coins + (packages[0].bonus_coins || 0));
          }
        }
        setPackagesError(null);
      } catch (error) {
        console.error("Error fetching coin packages:", error);
        // Show error - don't use fallback packages
        setPackagesError("Unable to load coin packages. Please try again later.");
        setCoinPackages([]);
      } finally {
        setLoadingPackages(false);
      }
    };

    if (openPaymentCheckout) {
      fetchCoinPackages();
    }
  }, [openPaymentCheckout]); // Removed showSnackbar from dependencies

  // Calculate coins based on dollar amount using actual package data
  const calculateCoins = useCallback((amount) => {
    if (!amount || amount <= 0) return 0;

    // If we have packages, calculate based on their rates
    if (coinPackages.length > 0) {
      // Find the closest package by price
      let bestRate = 100; // coins per dollar (default: 100 coins = $1)

      // Calculate rate from each package (coins per dollar)
      coinPackages.forEach((pkg) => {
        const totalCoins = pkg.coins + (pkg.bonus_coins || 0);
        const rate = totalCoins / pkg.price; // coins per dollar

        // Use the rate from a package close to our amount
        if (pkg.price <= amount * 1.5) {
          bestRate = Math.max(bestRate, rate);
        }
      });

      // If amount is higher than all packages, use the best rate we found
      const highestPackage = coinPackages.reduce((prev, current) =>
        (prev.price > current.price) ? prev : current
      );

      if (amount >= highestPackage.price) {
        const highestRate = (highestPackage.coins + (highestPackage.bonus_coins || 0)) / highestPackage.price;
        bestRate = highestRate;
      }

      return Math.floor(amount * bestRate);
    }

    // Fallback: default rate of 100 coins per dollar
    return Math.floor(amount * 100);
  }, [coinPackages]);

  // Update calculated coins when custom amount changes
  useEffect(() => {
    setCalculatedCoins(calculateCoins(customAmount));
  }, [customAmount, calculateCoins]);

  // Handle custom amount input change
  const handleAmountChange = (e) => {
    let inputValue = e.target.value;

    // Only allow numbers and one decimal point
    inputValue = inputValue.replace(/[^0-9.]/g, "");

    // Prevent multiple decimal points
    const parts = inputValue.split(".");
    if (parts.length > 2) {
      inputValue = parts[0] + "." + parts.slice(1).join("");
    }

    // Limit to 2 decimal places while typing
    if (parts.length === 2 && parts[1].length > 2) {
      inputValue = parts[0] + "." + parts[1].substring(0, 2);
    }

    // Update display immediately for responsive typing
    setDisplayAmount(inputValue);

    // Parse the value
    const amountValue = parseFloat(inputValue) || 0;

    // Update calculations in real-time
    setCustomAmount(amountValue);
    const coins = calculateCoins(amountValue);
    setCalculatedCoins(coins);

    // Find the closest matching package by price (for reference only)
    if (coinPackages.length > 0 && amountValue > 0) {
      let closestIndex = 0;
      let closestDiff = Math.abs(coinPackages[0].price - amountValue);

      coinPackages.forEach((pkg, index) => {
        const diff = Math.abs(pkg.price - amountValue);
        if (diff < closestDiff) {
          closestDiff = diff;
          closestIndex = index;
        }
      });

      setSelectedPackage(closestIndex);
    }
  };

  // Handle blur to format to 2 decimal places
  const handleAmountBlur = () => {
    if (customAmount > 0) {
      // Format to 2 decimal places when user leaves the field
      const formatted = customAmount.toFixed(2);
      setDisplayAmount(formatted);
    } else {
      // Clear if empty or zero
      setDisplayAmount("");
      setCalculatedCoins(0);
    }
  };

  // Handle focus to make editing easier
  const handleAmountFocus = (e) => {
    // Select all text for easy replacement
    e.target.select();
  };


  // Handle payment method selection
  const handlePaymentMethodSelect = (method) => {
    setSelectedPaymentMethod(method);
  };

  // Handle checkout process
  const handleCheckout = async () => {
    if (processing) return;

    // Check if a package is selected
    if (selectedPackage < 0 || !coinPackages[selectedPackage]) {
      showSnackbar("Please select a coin package", "warning");
      return;
    }

    const selectedPkg = coinPackages[selectedPackage];

    // Check if package has Stripe price ID
    if (!selectedPkg.stripe_price_id) {
      showSnackbar("This package is not yet available for purchase. Please select another package.", "warning");
      return;
    }

    setProcessing(true);

    try {
      if (selectedPaymentMethod === "stripe") {
        // Send the package ID and custom amount to backend
        const packageId = selectedPkg.id;
        // Pass custom amount if user entered a different amount than the package default
        const useCustomAmount = Math.abs(customAmount - selectedPkg.price) > 0.01;
        await handleStripePaymentCheckout(packageId, useCustomAmount ? customAmount : null);
      } else {
        // Handle Binance payment
        showSnackbar("Binance payment is not yet available", "warning");
      }
    } catch (error) {
      showSnackbar("Payment processing failed", "error");
      console.error("Payment error:", error);
    } finally {
      setTimeout(() => {
        setProcessing(false);
      }, 1000);
    }
  };

  return (
    <StyledDialog
      open={openPaymentCheckout}
      onClose={!processing ? handlePaymentCheckoutClose : undefined}
      TransitionComponent={Transition}
      fullWidth
      maxWidth="sm"
      aria-labelledby="payment-checkout-dialog"
    >
      <DialogTitle id="payment-checkout-dialog">
        <Box display="flex" alignItems="center">
          <CurrencyExchangeIcon
            sx={{ mr: 1.5, color: theme.palette.primary.main }}
          />
          <Typography variant="h6" component="span" fontWeight={600}>
            Purchase Coins
          </Typography>
        </Box>
        {!processing && (
          <IconButton
            aria-label="close"
            onClick={handlePaymentCheckoutClose}
            sx={{
              position: "absolute",
              right: 16,
              top: 16,
              color: theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent>
        {/* Loading/Error States */}
        {loadingPackages && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {packagesError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {packagesError}
          </Alert>
        )}

        {!loadingPackages && !packagesError && coinPackages.length === 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No coin packages available at the moment.
          </Alert>
        )}

        {!loadingPackages && !packagesError && coinPackages.length > 0 && (
          <>
            {/* Suggested Amounts - Generated Dynamically */}
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                sx={{ mb: 0.5 }}
              >
                Quick Select Amount
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", fontSize: "0.75rem" }}
              >
                Choose an amount or enter a custom value below
              </Typography>
            </Box>

            <PackageScrollContainer>
              {(() => {
                // Generate 5 suggested amounts based on the first package's coin rate
                const basePackage = coinPackages[0];
                const coinRate = (basePackage.coins + (basePackage.bonus_coins || 0)) / basePackage.price;
                const suggestedAmounts = [5, 10, 20, 35, 50];

                return suggestedAmounts.map((amount, index) => {
                  const totalCoins = Math.floor(amount * coinRate);
                  const isSelected = Math.abs(customAmount - amount) < 0.01;

                  return (
                    <Fade in timeout={300 + index * 60} key={`amount-${amount}`}>
                      <PackageCard
                        isselected={isSelected ? "true" : "false"}
                        onClick={() => {
                          setCustomAmount(amount);
                          setDisplayAmount(amount.toFixed(2));
                          setCalculatedCoins(totalCoins);
                          // Select the base package
                          setSelectedPackage(0);
                        }}
                        elevation={0}
                        sx={{
                          cursor: "pointer",
                          position: "relative",
                        }}
                      >
                        {/* Popular badge for middle option */}
                        {index === 2 && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: -6,
                              right: -6,
                              backgroundColor: "#10b981",
                              color: "white",
                              borderRadius: "50%",
                              width: 28,
                              height: 28,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "0.6rem",
                              boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                            }}
                          >
                            🔥
                          </Box>
                        )}

                        {/* Best value badge for last option */}
                        {index === 4 && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: -6,
                              right: -6,
                              backgroundColor: "#d44402",
                              color: "white",
                              borderRadius: "50%",
                              width: 28,
                              height: 28,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "0.6rem",
                              boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                            }}
                          >
                            ★
                          </Box>
                        )}

                        {/* Card content */}
                        <Box sx={{ textAlign: "center" }}>
                          {/* Price - Primary */}
                          <Typography
                            variant="h5"
                            sx={{
                              color: "primary.main",
                              fontWeight: 700,
                              fontSize: "1.75rem",
                              lineHeight: 1,
                              mb: 0.5,
                            }}
                          >
                            ${amount.toFixed(2)}
                          </Typography>

                          {/* Coin display - Secondary */}
                          <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5, justifyContent: "center" }}>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "text.primary",
                                fontWeight: 600,
                                fontSize: "0.95rem",
                              }}
                            >
                              {formatNumber(totalCoins)}
                            </Typography>

                            <Typography
                              variant="caption"
                              sx={{
                                color: "text.secondary",
                                fontSize: "0.7rem",
                              }}
                            >
                              coins
                            </Typography>
                          </Box>
                        </Box>
                      </PackageCard>
                    </Fade>
                  );
                });
              })()}
            </PackageScrollContainer>
          </>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Custom Amount */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ mb: 2 }}>
            Enter Custom Amount
          </Typography>

          <TextField
            variant="outlined"
            size="medium"
            fullWidth
            label="Amount (USD)"
            value={displayAmount}
            onChange={handleAmountChange}
            onBlur={handleAmountBlur}
            onFocus={handleAmountFocus}
            placeholder="0"
            error={customAmount > 0 && customAmount < 1}
            InputProps={{
              startAdornment: (
                <Box component="span" sx={{ mr: 1, opacity: 0.7, fontWeight: 600 }}>
                  $
                </Box>
              ),
            }}
            helperText={
              customAmount > 0 && customAmount < 1
                ? "Minimum amount is $1.00"
                : `Enter any amount between $1.00 and $${coinPackages.length > 0 ? (Math.max(...coinPackages.map(pkg => pkg.price)) * 2).toFixed(2) : '100.00'} • You'll receive ${formatNumber(calculatedCoins)} coins`
            }
            sx={{
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": {
                  borderColor: theme.palette.primary.main,
                },
              },
            }}
          />

          <Box
            sx={{
              mt: 2,
              p: 2,
              borderRadius: 2,
              backgroundColor: theme.palette.mode === "dark"
                ? "rgba(212, 68, 2, 0.1)"
                : "rgba(212, 68, 2, 0.05)",
              border: `1px solid ${theme.palette.mode === "dark" ? "rgba(212, 68, 2, 0.3)" : "rgba(212, 68, 2, 0.2)"}`,
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                You'll receive:
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="h5" color="primary" fontWeight={700}>
                  {formatNumber(calculatedCoins)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  coins
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="caption" color="text.secondary">
                Total Amount:
              </Typography>
              <Typography variant="h6" color="text.primary" fontWeight={600}>
                ${typeof customAmount === 'number' ? customAmount.toFixed(2) : '0.00'}
              </Typography>
            </Box>

            {/* Volume discount info - only show when valid amount */}
            {customAmount >= 1 && (
              <Tooltip
                title="Larger purchases get better coin rates with volume discounts"
                placement="bottom"
                TransitionComponent={Fade}
                arrow
              >
                <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
                  <InfoOutlinedIcon
                    fontSize="small"
                    sx={{ opacity: 0.5, cursor: "help" }}
                  />
                  <Typography variant="caption" color="text.disabled">
                    Volume discounts applied
                  </Typography>
                </Box>
              </Tooltip>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Payment Methods */}
        <Typography
          variant="subtitle1"
          fontWeight={500}
          gutterBottom
          sx={{ mb: 2 }}
        >
          Payment Method
        </Typography>

        <Stack spacing={2} sx={{ mb: 3 }}>
          <PaymentMethodButton
            startIcon={
              <Box
                component="span"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 30,
                  height: 30,
                  borderRadius: "50%",
                  backgroundColor: "#fff",
                }}
              >
                <img src={StripeIcon} width={20} height={20} alt="Stripe" />
              </Box>
            }
            isselected={selectedPaymentMethod === "stripe" ? "true" : "false"}
            onClick={() => handlePaymentMethodSelect("stripe")}
            fullWidth
          >
            Pay with Stripe
          </PaymentMethodButton>

          <PaymentMethodButton
            startIcon={
              <Box
                component="span"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 30,
                  height: 30,
                  borderRadius: "50%",
                  backgroundColor: "#F0B90B20",
                }}
              >
                <img src={BinanceIcon} width={20} height={20} alt="Binance" />
              </Box>
            }
            isselected={selectedPaymentMethod === "binance" ? "true" : "false"}
            onClick={() => handlePaymentMethodSelect("binance")}
            fullWidth
          >
            Pay with Binance Pay
          </PaymentMethodButton>
        </Stack>

        {/* Checkout Button */}
        <Button
          variant="contained"
          size="large"
          fullWidth
          startIcon={
            processing ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <ShoppingCartIcon />
            )
          }
          onClick={handleCheckout}
          disabled={processing || customAmount < 1}
          sx={{
            height: 48,
            borderRadius: 2,
            backgroundColor:
              theme.palette.mode === "dark" ? "#d44402" : "#d44402",
            "&:hover": {
              backgroundColor:
                theme.palette.mode === "dark" ? "#c03e02" : "#c03e02",
            },
          }}
        >
          {processing ? "Processing..." : `Checkout $${typeof customAmount === 'number' ? customAmount.toFixed(2) : '0.00'}`}
        </Button>
      </DialogContent>
    </StyledDialog>
  );
}
